import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle,
  Calculator,
  ArrowRight,
  Trash,
  PencilSimple,
  Share,
  Copy,
  ArrowsClockwise,
  CaretDown,
  CaretUp,
  Play,
} from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import MiniAppRunner from "@/components/MiniAppRunner";

const FinancialCalculator = () => {
  const [principal, setPrincipal] = useState(50000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(20);
  const [contrib, setContrib] = useState(500);

  const data = [];
  let bal = Number(principal);
  for (let y = 0; y <= Number(years); y++) {
    data.push({ year: y, balance: Math.round(bal) });
    bal = bal * (1 + Number(rate) / 100) + Number(contrib) * 12;
  }

  const final = data[data.length - 1].balance;
  const totalContrib = Number(principal) + Number(contrib) * 12 * Number(years);
  const growth = final - totalContrib;

  return (
    <div className="card-flat" data-testid="financial-calculator">
      <div className="flex items-center gap-3 mb-2">
        <Calculator size={20} className="text-[#A8977B]" />
        <div>
          <p className="label-eyebrow">Built-in Mini-App · Fully Functional</p>
          <h2 className="font-serif text-2xl text-[#112018]">Compound Growth Calculator</h2>
        </div>
      </div>
      <p className="text-sm text-[#606C64] mb-6 leading-relaxed">
        A working example of what a mini-app looks like once built. Adjust the inputs and
        the chart updates live.
      </p>

      <div className="grid lg:grid-cols-[1fr_2fr] gap-8">
        <div className="space-y-4">
          <NumField label="Initial principal ($)" value={principal} onChange={setPrincipal} />
          <NumField label="Annual return (%)" value={rate} onChange={setRate} step="0.1" />
          <NumField label="Years" value={years} onChange={setYears} />
          <NumField label="Monthly contribution ($)" value={contrib} onChange={setContrib} />
          <div className="border-t border-[#1A2F24]/12 pt-4 space-y-2">
            <Result label="Final balance" value={`$${final.toLocaleString()}`} accent />
            <Result label="Total contributions" value={`$${totalContrib.toLocaleString()}`} />
            <Result label="Growth from interest" value={`$${growth.toLocaleString()}`} />
          </div>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid stroke="rgba(26,47,36,0.08)" strokeDasharray="2 4" />
              <XAxis
                dataKey="year"
                stroke="#606C64"
                tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }}
              />
              <YAxis
                stroke="#606C64"
                tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                contentStyle={{
                  background: "#fff",
                  border: "1px solid rgba(26,47,36,0.2)",
                  borderRadius: 0,
                  fontFamily: "IBM Plex Mono",
                  fontSize: 12,
                }}
                formatter={(v) => `$${v.toLocaleString()}`}
              />
              <Line
                type="monotone"
                dataKey="balance"
                stroke="#1A2F24"
                strokeWidth={2}
                dot={{ r: 2, fill: "#A8977B" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const NumField = ({ label, value, onChange, step = "1" }) => (
  <div>
    <label className="label-eyebrow block mb-2">{label}</label>
    <input
      type="number"
      step={step}
      className="input-clean font-mono"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const Result = ({ label, value, accent }) => (
  <div className="flex justify-between items-baseline">
    <span className="label-eyebrow">{label}</span>
    <span className={`font-mono ${accent ? "text-[#A8977B] text-xl" : "text-[#1A2F24]"}`}>
      {value}
    </span>
  </div>
);

const SpecCard = ({ m, onChange }) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(m.description);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!window.confirm("Delete this mini-app spec?")) return;
    await api.delete(`/mini-apps/${m.id}`);
    toast.success("Deleted.");
    onChange();
  };

  const share = async () => {
    try {
      const r = await api.post(`/mini-apps/${m.id}/share`);
      try {
        await navigator.clipboard.writeText(r.data.url);
        toast.success("Share link copied to clipboard.");
      } catch {
        toast.success(`Share link ready: ${r.data.url}`);
      }
      onChange();
    } catch {
      toast.error("Share failed");
    }
  };

  const unshare = async () => {
    await api.delete(`/mini-apps/${m.id}/share`);
    toast.success("Share link revoked.");
    onChange();
  };

  const saveEdit = async (regenerate) => {
    setBusy(true);
    try {
      await api.patch(`/mini-apps/${m.id}`, { description: desc, regenerate });
      toast.success(regenerate ? "Spec regenerated." : "Description saved.");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const s = m.spec || {};
  const shareUrl = m.share_token
    ? `${window.location.origin}/m/${m.share_token}`
    : null;

  return (
    <div className="card-flat" data-testid={`mini-app-${m.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow">
            {s.category || "Custom"} ·{" "}
            {new Date(m.created_at).toLocaleDateString()}
          </p>
          <h3 className="font-serif text-3xl text-[#112018] mt-1">
            {s.title || "Untitled"}
          </h3>
          <p className="text-[#1A2F24] mt-2 text-sm leading-relaxed">{s.summary}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => setRunning(!running)}
            className={running ? "btn-primary px-2.5 py-1.5" : "btn-outline px-2.5 py-1.5"}
            data-testid={`run-${m.id}`}
            title="Run this mini-app"
          >
            <Play size={14} weight={running ? "fill" : "regular"} />
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="btn-outline px-2.5 py-1.5"
            data-testid={`expand-${m.id}`}
            title="View specification"
          >
            {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
          <button
            onClick={() => setEditing(!editing)}
            className="btn-outline px-2.5 py-1.5"
            data-testid={`edit-${m.id}`}
            title="Edit"
          >
            <PencilSimple size={14} />
          </button>
          <button
            onClick={share}
            className="btn-outline px-2.5 py-1.5"
            data-testid={`share-${m.id}`}
            title="Share publicly"
          >
            <Share size={14} />
          </button>
          <button
            onClick={remove}
            className="btn-outline px-2.5 py-1.5 hover:!border-[#C94A3D] hover:!text-[#C94A3D]"
            data-testid={`delete-${m.id}`}
            title="Delete"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>

      {shareUrl && (
        <div className="mt-3 flex items-center gap-2 p-2 bg-[#F7F6F2] border border-[#1A2F24]/12">
          <span className="label-eyebrow">Public:</span>
          <span className="font-mono text-[11px] text-[#1A2F24] truncate flex-1">
            {shareUrl}
          </span>
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Copied.");
              } catch {
                toast.message(shareUrl);
              }
            }}
            className="p-1 hover:bg-[#efece3]"
            data-testid={`copy-share-${m.id}`}
          >
            <Copy size={12} />
          </button>
          <button
            onClick={unshare}
            className="p-1 hover:bg-[#efece3] text-xs"
            data-testid={`unshare-${m.id}`}
            title="Revoke"
          >
            ×
          </button>
        </div>
      )}

      {editing && (
        <div className="mt-4 space-y-3 fade-up">
          <textarea
            data-testid={`edit-desc-${m.id}`}
            rows={3}
            className="input-clean"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              onClick={() => saveEdit(false)}
              disabled={busy}
              className="btn-outline"
              data-testid={`save-desc-${m.id}`}
            >
              Save Description
            </button>
            <button
              onClick={() => saveEdit(true)}
              disabled={busy}
              className="btn-primary"
              data-testid={`regenerate-${m.id}`}
            >
              <ArrowsClockwise size={14} className="inline mr-2" />
              {busy ? "Regenerating…" : "Save & Regenerate Spec"}
            </button>
          </div>
        </div>
      )}

      {running && (
        <div className="mt-6 pt-6 border-t border-[#1A2F24]/12 fade-up">
          <MiniAppRunner spec={s} />
        </div>
      )}

      {open && (
        <div className="mt-6 pt-6 border-t border-[#1A2F24]/12 grid md:grid-cols-2 gap-6 fade-up">
          {s.inputs?.length > 0 && (
            <div>
              <p className="label-eyebrow">Inputs</p>
              <ul className="mt-2 space-y-1 text-sm">
                {s.inputs.map((inp, i) => (
                  <li key={i} className="font-mono text-xs">
                    <span className="text-[#1A2F24]">{inp.label}</span>{" "}
                    <span className="text-[#606C64]">· {inp.type}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.outputs?.length > 0 && (
            <div>
              <p className="label-eyebrow">Outputs</p>
              <ul className="mt-2 space-y-1 text-sm">
                {s.outputs.map((o, i) => (
                  <li key={i}>
                    <span className="font-medium">{o.label}:</span>{" "}
                    <span className="text-[#606C64]">{o.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.user_stories?.length > 0 && (
            <div>
              <p className="label-eyebrow">User stories</p>
              <ul className="mt-2 space-y-1 list-disc pl-5 text-sm">
                {s.user_stories.map((st, i) => (
                  <li key={i}>{st}</li>
                ))}
              </ul>
            </div>
          )}
          {s.integration_points?.length > 0 && (
            <div>
              <p className="label-eyebrow">Integrations</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {s.integration_points.map((p, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 text-xs font-mono bg-[#1A2F24] text-[#F7F6F2]"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}
          {s.formula && (
            <div className="md:col-span-2 p-4 bg-[#F7F6F2] border border-[#1A2F24]/12">
              <p className="label-eyebrow">Formula</p>
              <p className="font-mono text-sm mt-2">{s.formula}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MiniAppsPage = () => {
  const [items, setItems] = useState([]);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/mini-apps");
    setItems(r.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    if (!desc.trim()) return;
    setBusy(true);
    try {
      await api.post("/mini-apps/generate", { description: desc });
      toast.success("Mini-app spec generated.");
      setDesc("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="mini-apps-page" className="space-y-8">
      <div className="pb-8 border-b border-[#1A2F24]/12">
        <p className="label-eyebrow">Custom Tools</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2">Mini-Apps</h1>
        <p className="text-[#606C64] mt-2 text-sm max-w-2xl">
          A library of small tools built for your firm. The Compound Growth Calculator
          below is fully functional; generated specs below are blueprints that engineering
          can turn into live tools.
        </p>
      </div>

      <FinancialCalculator />

      <div className="card-flat">
        <div className="flex items-center gap-3 mb-4">
          <Sparkle size={20} className="text-[#A8977B]" />
          <div>
            <p className="label-eyebrow">Build a custom mini-app</p>
            <h2 className="font-serif text-2xl text-[#112018]">Describe it</h2>
          </div>
        </div>
        <textarea
          data-testid="mini-app-description"
          className="input-clean"
          rows={4}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="e.g. A tool that estimates quarterly estimated tax payments based on YTD income, posts a reminder to Slack 5 days before each due date, and writes the schedule to a Google Sheet."
        />
        <button
          onClick={generate}
          disabled={busy}
          className="btn-primary mt-4"
          data-testid="generate-mini-app-btn"
        >
          {busy ? "Generating spec…" : "Generate Specification"}
          <ArrowRight size={14} className="inline ml-2" />
        </button>
      </div>

      <div>
        <p className="label-eyebrow mb-4">
          Your library · {items.length} {items.length === 1 ? "spec" : "specs"}
        </p>
        {items.length === 0 ? (
          <div className="card-flat text-center py-12">
            <p className="font-serif text-2xl text-[#112018]">No specs yet.</p>
            <p className="text-sm text-[#606C64] mt-1">
              Describe a tool above to generate your first spec.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((m) => (
              <SpecCard key={m.id} m={m} onChange={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniAppsPage;
